<template>
  <section v-if="visibleSlides.length" class="carousel-block">
    <div class="carousel-block__card">
      <h2 v-if="data.title && String(data.title).trim()" class="carousel-block__title text-circe-bold-22 color-green">
        {{ data.title }}
      </h2>
      <div
        class="carousel-block__viewport"
        @mouseenter="onHoverEnter"
        @mouseleave="onHoverLeave"
      >
        <button
          v-if="visibleSlides.length > 1"
          type="button"
          class="carousel-block__arrow carousel-block__arrow--prev"
          aria-label="Предыдущий слайд"
          @click="goPrev"
        >
          ‹
        </button>
        <button
          v-if="visibleSlides.length > 1"
          type="button"
          class="carousel-block__arrow carousel-block__arrow--next"
          aria-label="Следующий слайд"
          @click="goNext"
        >
          ›
        </button>
        <div class="carousel-block__mask">
          <div
            class="carousel-block__track"
            :style="{ transform: `translateX(-${100 * currentIndex}%)` }"
          >
            <div
              v-for="(item, i) in visibleSlides"
              :key="'slide-' + item.index"
              class="carousel-block__slide"
            >
              <div class="carousel-block__slide-inner">
                <button
                  type="button"
                  class="carousel-block__img-wrap"
                  :aria-label="slideZoomLabel(item.slide)"
                  @click="openLightbox(i)"
                >
                  <CarouselSlideImageView
                    :slide="item.slide"
                    :caption="slideCaption(item.slide)"
                  />
                  <span class="carousel-block__zoom-hint" aria-hidden="true" />
                </button>
                <a
                  v-if="slideHref(item.slide) && slideCaption(item.slide)"
                  :href="slideHref(item.slide)"
                  class="carousel-block__caption carousel-block__caption--link text-circe-18 color-gray"
                  :target="isExternal(item.slide.link) ? '_blank' : undefined"
                  :rel="isExternal(item.slide.link) ? 'noopener noreferrer' : undefined"
                >
                  {{ slideCaption(item.slide) }}
                </a>
                <p
                  v-else-if="slideCaption(item.slide)"
                  class="carousel-block__caption text-circe-18 color-gray"
                >
                  {{ slideCaption(item.slide) }}
                </p>
                <a
                  v-if="slideHref(item.slide) && !slideCaption(item.slide)"
                  :href="slideHref(item.slide)"
                  class="carousel-block__more-link text-circe-18 color-green"
                  :target="isExternal(item.slide.link) ? '_blank' : undefined"
                  :rel="isExternal(item.slide.link) ? 'noopener noreferrer' : undefined"
                >
                  Подробнее
                </a>
              </div>
            </div>
          </div>
        </div>
        <div v-if="visibleSlides.length > 1" class="carousel-block__dots" role="tablist" aria-label="Слайды">
          <button
            v-for="(item, i) in visibleSlides"
            :key="'dot-' + item.index"
            type="button"
            class="carousel-block__dot"
            :class="{ 'carousel-block__dot--active': i === currentIndex }"
            :aria-label="'Слайд ' + (i + 1)"
            :aria-current="i === currentIndex ? 'true' : undefined"
            @click="goTo(i)"
          />
        </div>
      </div>
    </div>
    <LightBox
      ref="lightbox"
      :media="lightboxMedia"
      :show-light-box="lightboxVisible"
      :site-loading="siteLoading"
      @close="closeLightbox"
    />
  </section>
</template>

<script>
import LightBox from 'vue-image-lightbox';
import 'vue-image-lightbox/dist/vue-image-lightbox.min.css';
import siteLoading from '@/assets/img/siteloading.gif';
import CarouselSlideImageView from '@/components/Blocks/CarouselSlideImageView.vue';

export default {
  name: 'AppCarouselBlock',
  components: { CarouselSlideImageView, LightBox },
  props: {
    data: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data() {
    return {
      currentIndex: 0,
      hoverPause: false,
      autoplayTimer: null,
      lightboxVisible: false,
      siteLoading,
    };
  },
  computed: {
    lightboxMedia() {
      return this.visibleSlides.map(({ slide }) => {
        const caption = this.slideCaption(slide);
        return {
          src: String(slide.image || '').trim(),
          thumb: String(slide.image || '').trim(),
          alt: caption || 'Фото',
          caption: caption || '',
        };
      });
    },
    visibleSlides() {
      const raw = this.data && Array.isArray(this.data.slides) ? this.data.slides : [];
      return raw
        .map((s, index) => ({ index, slide: s }))
        .filter(({ slide: s }) => s && String(s.image || '').trim());
    },
    intervalMs() {
      const sec = Number(this.data && this.data.intervalSec);
      const s = Number.isFinite(sec) ? sec : 5;
      return Math.min(60000, Math.max(2000, Math.round(s * 1000)));
    }
  },
  watch: {
    data: {
      deep: true,
      handler() {
        this.clampIndex();
        this.restartAutoplay();
      }
    },
    visibleSlides: {
      handler() {
        this.clampIndex();
        this.restartAutoplay();
      },
      deep: true
    }
  },
  mounted() {
    this.startAutoplay();
  },
  beforeDestroy() {
    this.clearAutoplay();
  },
  methods: {
    slideCaption(slide) {
      return (slide.caption || '').trim();
    },
    slideHref(slide) {
      const h = (slide.link || '').trim();
      return h || null;
    },
    slideZoomLabel(slide) {
      const caption = this.slideCaption(slide);
      return caption ? `Увеличить: ${caption}` : 'Увеличить фото';
    },
    openLightbox(index) {
      if (!this.lightboxMedia.length) return;
      this.lightboxVisible = true;
      this.$nextTick(() => {
        if (this.$refs.lightbox) {
          this.$refs.lightbox.showImage(index);
        }
      });
    },
    closeLightbox() {
      this.lightboxVisible = false;
    },
    isExternal(link) {
      return /^https?:\/\//i.test((link || '').trim());
    },
    clampIndex() {
      const n = this.visibleSlides.length;
      if (n === 0) {
        this.currentIndex = 0;
        return;
      }
      if (this.currentIndex >= n) {
        this.currentIndex = n - 1;
      }
      if (this.currentIndex < 0) {
        this.currentIndex = 0;
      }
    },
    goTo(i) {
      if (i < 0 || i >= this.visibleSlides.length) return;
      this.currentIndex = i;
      this.restartAutoplay();
    },
    goNext() {
      const n = this.visibleSlides.length;
      if (n < 2) return;
      this.currentIndex = (this.currentIndex + 1) % n;
      this.restartAutoplay();
    },
    goPrev() {
      const n = this.visibleSlides.length;
      if (n < 2) return;
      this.currentIndex = (this.currentIndex - 1 + n) % n;
      this.restartAutoplay();
    },
    clearAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },
    startAutoplay() {
      this.clearAutoplay();
      if (this.hoverPause || this.visibleSlides.length < 2) return;
      this.autoplayTimer = setInterval(() => {
        this.goNext();
      }, this.intervalMs);
    },
    restartAutoplay() {
      this.clearAutoplay();
      this.$nextTick(() => this.startAutoplay());
    },
    onHoverEnter() {
      this.hoverPause = true;
      this.clearAutoplay();
    },
    onHoverLeave() {
      this.hoverPause = false;
      this.startAutoplay();
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/colorVariables.scss";

.carousel-block {
  padding: 0 0 20px;
}

.carousel-block__card {
  max-width: 1650px;
  margin: 0 auto;
  font-family: 'Circe', Arial, sans-serif;
  background-color: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.carousel-block__title {
  margin: 0 0 20px 0;
  text-align: center;
}

.carousel-block__viewport {
  position: relative;
  user-select: none;
}

.carousel-block__mask {
  overflow: hidden;
  border-radius: 12px;
  background: #f5f5f5;
}

.carousel-block__track {
  display: flex;
  transition: transform 0.45s ease;
  will-change: transform;
}

.carousel-block__slide {
  flex: 0 0 100%;
  min-width: 100%;
  box-sizing: border-box;
}

.carousel-block__slide-inner {
  display: block;
  color: inherit;
}

.carousel-block__img-wrap {
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: zoom-in;
  position: relative;
  text-align: left;
  font: inherit;
  color: inherit;

  &:focus {
    outline: 2px solid $color-index-green;
    outline-offset: 2px;
  }
}

.carousel-block__zoom-hint {
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.35-4.35M11 8v6M8 11h6'/%3E%3C/svg%3E") center / 18px no-repeat;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s;
}

.carousel-block__img-wrap:hover .carousel-block__zoom-hint,
.carousel-block__img-wrap:focus .carousel-block__zoom-hint {
  opacity: 1;
}

.carousel-block__caption {
  margin: 0;
  padding: 14px 16px 4px;
  text-align: center;
  line-height: 1.4;
}

.carousel-block__caption--link {
  display: block;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: $color-index-green;
  }
}

.carousel-block__more-link {
  display: inline-block;
  margin: 8px 16px 4px;
  text-align: center;
  width: calc(100% - 32px);
  box-sizing: border-box;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.carousel-block__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  color: #333;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 4px;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: $color-index-green;
    color: #fff;
  }

  &:focus {
    outline: 2px solid $color-index-green;
    outline-offset: 2px;
  }
}

.carousel-block__arrow--prev {
  left: 10px;
}

.carousel-block__arrow--next {
  right: 10px;
}

.carousel-block__dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 16px 0 0;
}

.carousel-block__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  padding: 0;
  background: #ccc;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;

  &:hover {
    background: #999;
  }
}

.carousel-block__dot--active {
  background: $color-index-green;
  transform: scale(1.15);
}

@media (max-width: 1024px) {
  .carousel-block__card {
    padding: 30px;
  }
}

@media (max-width: 768px) {
  .carousel-block {
    margin-left: -15px;
    margin-right: -15px;
    width: calc(100% + 30px);
    padding-bottom: 16px;
  }

  .carousel-block__card {
    padding: 14px 10px 12px;
    border-radius: 12px;
  }

  .carousel-block__title {
    margin-bottom: 12px;
    font-size: 18px;
    line-height: 1.25;
  }

  .carousel-block__mask {
    border-radius: 8px;
  }

  .carousel-block__img-wrap ::v-deep .carousel-slide-img {
    aspect-ratio: 4 / 3;
  }

  .carousel-block__zoom-hint {
    opacity: 0.85;
  }

  .carousel-block__arrow {
    width: 38px;
    height: 38px;
    font-size: 24px;
  }

  .carousel-block__arrow--prev {
    left: 6px;
  }

  .carousel-block__arrow--next {
    right: 6px;
  }

  .carousel-block__dots {
    padding-top: 12px;
  }

  .carousel-block__caption {
    padding: 10px 8px 2px;
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .carousel-block {
    margin-left: -10px;
    margin-right: -10px;
    width: calc(100% + 20px);
  }

  .carousel-block__card {
    padding: 10px 6px 8px;
    border-radius: 10px;
  }

  .carousel-block__caption {
    padding: 8px 4px 0;
  }
}

@media (max-width: 360px) {
  .carousel-block {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
  }
}
</style>
